import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Accordion from '../../../components/Accordion/Accordion';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Academic Research Solutions | General Genomics"
        description="Learn how General Genomics supports academic institutions with synthetic and de-identified data. Drive groundbreaking research while ensuring data privacy, compliance, and fostering collaboration in biosciences."
      />
      <Hero
        helper="Industries &gt; Commercial"
        headline="Data solutions for transformative academic research"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Unlock Advanced Data Solutions for Research
        </h2>
        <p className="action-on-view action-fade-in">
          At General Genomics, we provide academic institutions with
          cutting-edge synthetic and de-identified data solutions to fuel
          breakthrough research. Our data ensures privacy compliance while
          enabling innovation across various biosciences fields. Collaborate
          with us to access tailored data that enhances your research
          capabilities and accelerates your discoveries.
        </p>
        <Image
          path="/images/library/office-woman-sitting.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/lab-technicians-group-laptop-6.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">
          Empowering Research Innovation
        </p>
        <h2 className="action-on-view action-fade-in">
          Our Data Solutions for Academic Research
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Advanced Data Solutions:</strong> Harness synthetic and
            de-identified data to push the boundaries of research in
            biosciences, ensuring privacy while advancing innovation.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Privacy and Compliance:</strong> We adhere to the highest
            data privacy standards, ensuring all solutions are fully compliant
            with regulations to protect sensitive information.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Collaborative Research:</strong> Partner with General
            Genomics to gain access to our extensive datasets and work together
            on groundbreaking discoveries in various fields.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Tailored Solutions:</strong> Get customized data solutions
            designed to meet the specific needs of your research projects,
            ensuring you have the right tools to succeed.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More of Our Genomic Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how our data solutions, tools, and resources can accelerate
          your academic research and innovation in genomics.
        </p>
        <div className="card-group">
          <Card
            icon="data_usage"
            headline="Synthetic & De-Identified Data"
            subhead="Learn how our data solutions support academic research with secure, anonymized datasets."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="biotech"
            headline="Curo46™: Innovation Tool"
            subhead="Explore Curo46™ and its advanced capabilities for groundbreaking genomic research."
            btnURL="/innovation/curo46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="cloud_download"
            headline="Data Portal"
            subhead="Access a wide range of genomics datasets to power your research projects."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/office-group-data.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; academic
          research solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics supports academic research with synthetic
          data, privacy compliance, and tailored data solutions for innovation.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What types of data does General Genomics provide for academic research?"
          >
            General Genomics offers synthetic and de-identified data, designed
            to support academic research while ensuring privacy and compliance
            with regulations.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure data privacy and compliance?"
          >
            We adhere to strict privacy regulations and compliance standards,
            ensuring that all data shared with academic institutions is
            de-identified and secure.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What research areas can benefit from General Genomics' data solutions?"
          >
            Our data solutions support a wide range of biosciences research,
            including general genomics, drug discovery, and disease modeling,
            helping drive groundbreaking discoveries.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can General Genomics provide customized data solutions for my research project?"
          >
            Yes, we offer tailored data solutions designed to meet the specific
            needs of your academic research.{' '}
            <Link to="/contact-us">Contact us</Link> for more details.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I get started with General Genomics for my academic research?"
          >
            Reach out to us through our contact page to explore how we can
            support your research.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
